import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BarChart2, Database, Shield, LineChart, Cog, CheckCircle2, TrendingUp, Clock, HelpCircle, ChevronDown } from 'lucide-react';
import SEOHead from '../../components/SEOHead';

const keyMetrics = [
  {
    icon: TrendingUp,
    value: '80%',
    label: 'Faster Reporting',
    description: 'Average reduction in time from data to delivered insight'
  },
  {
    icon: CheckCircle2,
    value: '99.9%',
    label: 'Data Accuracy',
    description: 'Through automated validation and quality frameworks'
  },
  {
    icon: LineChart,
    value: '30%+',
    label: 'Cost Reduction',
    description: 'In reporting infrastructure through cloud-native BI platforms'
  },
  {
    icon: Clock,
    value: 'Real-Time',
    label: 'Visibility',
    description: 'Live dashboards replacing manual report generation cycles'
  }
];

const services = [
  {
    title: 'Dashboards & Operational Reporting',
    icon: BarChart2,
    items: [
      'Executive Dashboards: KPI-driven real-time visibility across revenue, operations, and performance',
      'Operational Reports: Automated generation and distribution replacing manual spreadsheet workflows',
      'Paginated Reports: Pixel-perfect formatted reports for financial, compliance, and regulatory output',
      'Mobile-Ready BI: Dashboards accessible across desktop, tablet, and mobile with consistent performance'
    ]
  },
  {
    title: 'Data Warehouse & Analytics Infrastructure',
    icon: Database,
    items: [
      'Modern Warehousing: Star and Snowflake schema design on Snowflake, BigQuery, and Azure Synapse',
      'ETL & ELT Pipelines: Automated data ingestion from CRMs, ERPs, APIs, and transactional systems',
      'Data Quality Frameworks: Automated validation, cleansing, and reconciliation ensuring a single version of truth',
      'Historical & Trend Analysis: Multi-year datasets structured for cohort analysis, forecasting, and benchmarking'
    ]
  },
  {
    title: 'Advanced Analytics & AI-Driven Insights',
    icon: TrendingUp,
    items: [
      'Predictive Analytics: Demand forecasting, churn prediction, and revenue modelling using XGBoost and ensemble ML',
      'Customer Segmentation: Behavioural clustering and cohort analysis for targeted decision-making',
      'Funnel & Conversion Analysis: Drop-off identification and journey tracking across customer touchpoints',
      'Recommendation Intelligence: AI-powered product and content recommendation engines with CTR tracking'
    ]
  },
  {
    title: 'Financial & Commission Reporting',
    icon: Shield,
    items: [
      'Merchant & Agent Reporting: Automated commission calculation systems handling complex tiered structures',
      'Tax & Compliance Reporting: GST, TDS, VAT calculation logic built directly into reporting pipelines',
      'Payment Reconciliation: Automated matching of transactions against ledger entries with exception flagging',
      'Multi-Currency & Multi-Market: Reporting infrastructure supporting regional expansion and currency conversion'
    ]
  }
];

const techStack = [
  {
    category: 'BI Platforms',
    items: [
      { name: 'Power BI', logo: '/tech-logos/power-bi.png' },
      { name: 'Tableau', logo: '/tech-logos/tableau.png' },
      { name: 'Looker', logo: '/tech-logos/looker.png' },
      { name: 'Zoho Analytics', logo: '/tech-logos/zoho.png' }
    ]
  },
  {
    category: 'Data Warehouses',
    items: [
      { name: 'Snowflake', logo: '/tech-logos/snowflake.svg' },
      { name: 'Google BigQuery', logo: '/tech-logos/google-cloud.svg' },
      { name: 'Azure Synapse', logo: '/tech-logos/azure-logo.svg' },
      { name: 'Amazon Redshift', logo: '/tech-logos/aws.svg' }
    ]
  },
  {
    category: 'Orchestration & Pipelines',
    items: [
      { name: 'Apache Airflow', logo: '/tech-logos/airflow.svg' },
      { name: 'Azure Data Factory', logo: '/tech-logos/azure-logo.svg' },
      { name: 'dbt', logo: '/tech-logos/dbt.svg' },
      { name: 'Google Cloud Dataflow', logo: '/tech-logos/google-cloud.svg' }
    ]
  },
  {
    category: 'Analytics & ML',
    items: [
      { name: 'Python', logo: '/tech-logos/python.svg' },
      { name: 'scikit-learn', logo: '/tech-logos/python.svg' },
      { name: 'TensorFlow', logo: '/tech-logos/tensorflow.svg' },
      { name: 'Pandas / NumPy', logo: '/tech-logos/python.svg' }
    ]
  }
];

const industries = [
  {
    title: 'Healthcare',
    icon: Shield,
    applications: [
      'Clinical performance dashboards',
      'Patient outcome and safety metric tracking',
      'Compliance and regulatory reporting'
    ]
  },
  {
    title: 'FMCG & Manufacturing',
    icon: Cog,
    applications: [
      'Supply chain and inventory visibility',
      'Sales distribution and territory performance',
      'Operational cost and production reporting'
    ]
  },
  {
    title: 'Financial Services & Insurance',
    icon: LineChart,
    applications: [
      'Brokerage statement and commission analytics',
      'Risk and portfolio performance dashboards',
      'Regulatory and audit reporting'
    ]
  },
  {
    title: 'Food Delivery & Logistics',
    icon: TrendingUp,
    applications: [
      'Order, delivery, and revenue analytics',
      'Agent and merchant performance tracking',
      'Funnel and conversion analysis across markets'
    ]
  }
];

const successStories = [
  {
    tag: 'FMCG',
    title: 'FMCG Enterprise — Reporting Modernisation',
    metrics: [
      '1,000+ SSRS reports converted to Power BI Paginated Reports',
      '30% faster report rendering post-migration',
      '50% reduction in reporting maintenance effort'
    ],
    link: '/case-studies/fmcg-migration'
  },
  {
    tag: 'Food Delivery',
    title: 'Food Delivery Platform — AI Analytics Infrastructure',
    metrics: [
      '41.85% click-through rate on AI-powered recommendations',
      '$1M+ monthly transactions tracked through automated reporting',
      'Real-time dashboards across Snowflake, BigQuery, and Zoho Analytics'
    ],
    link: '/case-studies/payment-automation'
  },
  {
    tag: 'Pharmaceutical Marketing',
    title: 'Pharmaceutical Marketing Platform — Multi-Client Analytics',
    metrics: [
      '95%+ data accuracy across 15+ client deployments',
      '80% faster client onboarding through standardised reporting templates',
      '100% automated physician-level data reporting with NPI compliance'
    ],
    link: '/case-studies/pharma-analytics'
  }
];

const methodology = [
  {
    title: 'Data Discovery & Audit',
    description: 'Inventory of all existing data sources, reports, and quality issues',
    icon: Database
  },
  {
    title: 'Architecture Design',
    description: 'Warehouse schema, pipeline design, and BI platform selection',
    icon: BarChart2
  },
  {
    title: 'Pipeline Development',
    description: 'ETL/ELT build with validation, testing, and error handling',
    icon: Cog
  },
  {
    title: 'Dashboard Development',
    description: 'Report and dashboard build with iterative stakeholder review',
    icon: LineChart
  },
  {
    title: 'Testing & Validation',
    description: 'Data accuracy validation against source systems before go-live',
    icon: CheckCircle2
  },
  {
    title: 'Training & Handover',
    description: 'User training, documentation, and ongoing optimisation support',
    icon: TrendingUp
  }
];

const whyChooseUs = [
  {
    title: 'Production-Grade Quality',
    description: 'Every dashboard and pipeline is built to enterprise standards with monitoring, alerting, and automated validation baked in from day one.',
    icon: Shield
  },
  {
    title: 'Cross-Platform Expertise',
    description: 'Deep hands-on experience across Power BI, Tableau, Snowflake, BigQuery, and Zoho Analytics — we work with what fits your environment.',
    icon: BarChart2
  },
  {
    title: 'Finance-Ready Output',
    description: 'Our reporting systems handle commission structures, tax calculations, and reconciliation logic that generic BI tools cannot.',
    icon: LineChart
  },
  {
    title: 'End-to-End Ownership',
    description: 'From raw data ingestion through to delivered dashboard, we own the full stack — no gaps, no handoffs between vendors.',
    icon: CheckCircle2
  }
];

const faqs = [
  {
    question: 'How long does a BI implementation take?',
    answer: 'A standard dashboard and pipeline implementation runs 4–8 weeks depending on data source complexity and number of reports. Large-scale migrations (1,000+ reports) are scoped separately with phased delivery.'
  },
  {
    question: 'Can you work with our existing BI platform?',
    answer: 'Yes. We work across Power BI, Tableau, Looker, Zoho Analytics, and custom web-based dashboards. We optimise existing environments as well as build new ones.'
  },
  {
    question: 'Do you handle data migrations from legacy reporting systems like SSRS?',
    answer: 'Yes. We have direct experience migrating large SSRS estates to Power BI Paginated Reports with high conversion success rates and preserved formatting and parameterisation.'
  },
  {
    question: 'What if our data quality is poor?',
    answer: 'Data quality remediation is part of our standard process. We build validation and cleansing logic into every pipeline so dashboards reflect accurate data from the start.'
  }
];

const BIAnalyticsSolution = () => {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  return (
    <>
      <SEOHead
        title="BI & Analytics Solutions | Agility.AI"
        description="Transform raw data into real-time decisions through enterprise-grade dashboards, automated reporting, and advanced analytics."
        keywords="business intelligence, analytics, Power BI, Tableau, data warehouse, Snowflake, BigQuery, dashboards, reporting"
        url="https://www.agilitytech.ai/solutions/bi-analytics"
      />
      <div className="pt-20">
        {/* Hero Section */}
        <section className="relative py-24 bg-gradient-to-r from-blue-900 to-black overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80"
              alt="BI Analytics Banner"
              className="w-full h-full object-cover opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent" />
          </div>
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="max-w-4xl">
              <div className="inline-flex items-center px-4 py-2 bg-teal-500/10 rounded-full border border-teal-500/20 mb-6">
                <span className="text-teal-400 text-sm font-medium">Business Intelligence & Analytics</span>
              </div>
              <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
                Business Intelligence & Analytics Solutions
              </h1>
              <p className="text-xl text-gray-300 leading-relaxed mb-8 max-w-3xl">
                Transform raw data into real-time decisions through enterprise-grade dashboards, automated
                reporting, and advanced analytics that give every level of your organisation instant
                visibility into what matters.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  className="bg-teal-500 text-white px-8 py-4 rounded-lg text-lg font-medium
                    hover:bg-teal-400 transition-all duration-300 inline-flex items-center space-x-2
                    shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                  <span>Schedule Your Analytics Assessment</span>
                  <ArrowRight className="h-5 w-5" />
                </Link>
                <button
                  className="bg-white/10 text-white px-8 py-4 rounded-lg text-lg font-medium
                    hover:bg-white/20 transition-all duration-300 inline-flex items-center space-x-2
                    border border-white/20 hover:border-white/30"
                >
                  <span>Download Solutions Guide</span>
                  <ArrowRight className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Key Metrics Section */}
        <section className="py-16 bg-black">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {keyMetrics.map((metric, index) => (
                <div key={index} className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-6
                  border border-gray-800/50 hover:bg-gray-900/70 transition-all duration-300 text-center">
                  <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <metric.icon className="h-6 w-6 text-teal-400" />
                  </div>
                  <div className="text-3xl font-bold text-white mb-2">{metric.value}</div>
                  <div className="text-teal-400 font-medium mb-2">{metric.label}</div>
                  <p className="text-gray-400 text-sm">{metric.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* What We Build Section */}
        <section className="py-20 bg-black">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">
              What We Build
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {services.map((service, index) => (
                <div
                  key={index}
                  className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-8
                    border border-gray-800/50 hover:bg-gray-900/70 transition-all duration-300
                    transform hover:-translate-y-1"
                >
                  <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center
                    justify-center mb-6">
                    <service.icon className="h-6 w-6 text-teal-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-4">{service.title}</h3>
                  <ul className="space-y-3">
                    {service.items.map((item, idx) => (
                      <li key={idx} className="flex items-start space-x-3 text-gray-300">
                        <CheckCircle2 className="h-5 w-5 text-teal-400 flex-shrink-0 mt-1" />
                        <span className="text-sm leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Technology Stack Section */}
        <section className="py-20 bg-gray-900">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">
              Technology Stack
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {techStack.map((category, index) => (
                <div key={index} className="space-y-6">
                  <h3 className="text-xl font-bold text-teal-400 mb-4 text-center">{category.category}</h3>
                  <div className="grid grid-cols-1 gap-4">
                    {category.items.map((item, idx) => (
                      <div
                        key={idx}
                        className="flex items-center space-x-3 bg-gray-800/50 rounded-lg p-4
                          border border-gray-700/50 hover:bg-gray-800/70 transition-all duration-300"
                      >
                        <img
                          src={item.logo}
                          alt={`${item.name} logo`}
                          className="h-8 w-8 object-contain"
                        />
                        <span className="text-gray-300 font-medium">{item.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Industry Applications Section */}
        <section className="py-20 bg-black">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">
              Industry Applications
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {industries.map((industry, index) => (
                <div
                  key={index}
                  className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-8
                    border border-gray-800/50 hover:bg-gray-900/70 transition-all duration-300
                    transform hover:-translate-y-1"
                >
                  <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center
                    justify-center mb-6">
                    <industry.icon className="h-6 w-6 text-teal-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-4">{industry.title}</h3>
                  <ul className="space-y-3">
                    {industry.applications.map((app, idx) => (
                      <li key={idx} className="flex items-start space-x-3 text-gray-300">
                        <ArrowRight className="h-5 w-5 text-teal-400 flex-shrink-0 mt-1" />
                        <span className="text-sm leading-relaxed">{app}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Success Stories Section */}
        <section className="py-20 bg-gray-900">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">
              Featured Success Stories
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {successStories.map((story, index) => (
                <div
                  key={index}
                  className="bg-gray-800/50 rounded-xl p-8 border border-gray-700/50
                    hover:bg-gray-800/70 transition-all duration-300 transform hover:-translate-y-1"
                >
                  <div className="text-sm text-teal-400 font-semibold mb-4">{story.tag}</div>
                  <h3 className="text-xl font-bold text-white mb-6">{story.title}</h3>
                  <div className="space-y-4 mb-6">
                    {story.metrics.map((metric, idx) => (
                      <div key={idx} className="flex items-center space-x-3 text-gray-300">
                        <CheckCircle2 className="h-4 w-4 text-green-400" />
                        <span className="text-sm">{metric}</span>
                      </div>
                    ))}
                  </div>
                  <Link
                    to={story.link}
                    className="inline-flex items-center text-teal-400 hover:text-teal-300
                      transition-colors font-medium"
                  >
                    <span>Read Full Case Study</span>
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Methodology Section */}
        <section className="py-20 bg-black">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">
              Our Methodology
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {methodology.map((step, index) => (
                <div
                  key={index}
                  className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-6
                    border border-gray-800/50 hover:bg-gray-900/70 transition-all duration-300"
                >
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="w-8 h-8 bg-teal-500/10 rounded-full flex items-center
                      justify-center text-teal-400 font-bold">
                      {index + 1}
                    </div>
                    <div className="w-8 h-8 bg-teal-500/10 rounded-lg flex items-center justify-center">
                      <step.icon className="h-5 w-5 text-teal-400" />
                    </div>
                  </div>
                  <h3 className="text-lg font-bold text-white mb-3">{step.title}</h3>
                  <p className="text-gray-300 text-sm leading-relaxed">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Agility.AI Section */}
        <section className="py-20 bg-gray-900">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">
              Why Choose Agility.AI
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {whyChooseUs.map((reason, index) => (
                <div
                  key={index}
                  className="bg-gray-800/50 rounded-xl p-8 border border-gray-700/50
                    hover:bg-gray-800/70 transition-all duration-300 text-center"
                >
                  <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center
                    justify-center mb-6 mx-auto">
                    <reason.icon className="h-6 w-6 text-teal-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{reason.title}</h3>
                  <p className="text-gray-300 text-sm leading-relaxed">{reason.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-black">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <HelpCircle className="h-12 w-12 text-teal-400 mx-auto mb-4" />
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  Frequently Asked Questions
                </h2>
                <p className="text-xl text-gray-400">
                  Everything you need to know about our BI & analytics implementation process
                </p>
              </div>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div
                    key={index}
                    className="bg-gray-900/50 backdrop-blur-sm rounded-xl border border-gray-800/50 overflow-hidden"
                  >
                    <button
                      onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                      className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-900/70 transition-all duration-300"
                    >
                      <span className="text-lg font-semibold text-white pr-8">{faq.question}</span>
                      <ChevronDown
                        className={`h-5 w-5 text-teal-400 flex-shrink-0 transition-transform duration-300 ${
                          openFaqIndex === index ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    {openFaqIndex === index && (
                      <div className="px-6 pb-5">
                        <p className="text-gray-300 leading-relaxed">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-20 bg-gradient-to-r from-blue-900 to-black">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="max-w-3xl mx-auto">
              <div className="w-16 h-16 bg-teal-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <BarChart2 className="h-8 w-8 text-teal-400" />
              </div>
              <h2 className="text-4xl font-bold text-white mb-6">
                Ready to Transform Your Reporting?
              </h2>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                Schedule an analytics assessment with our BI specialists to discover how modern
                dashboards and data pipelines can accelerate your decision-making.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center space-x-3 bg-teal-500 text-white px-8 py-4
                  rounded-lg text-lg font-medium hover:bg-teal-400 transition-all duration-300 group
                  shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                <span>Schedule Your Analytics Assessment</span>
                <ArrowRight className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default BIAnalyticsSolution;
